import { type FC, useEffect } from 'react';

import type { DecompositionNode } from 'api/data/projects/types';

import { ChildrenSubscribers } from '../../components/ChildrenSubscribers';
import { useTreeDataLayer } from '../../hooks/useTreeDataLayer';
import { getDataNodeIdFromTreeId } from '../../utils/treeId';
import { useMoveDialogSession } from '../hooks/useMoveDialogSession';
import type { MoveDialogTarget } from '../hooks/useRequestMove';
import { MoveNodeDialog } from './MoveNodeDialog';

type MoveMutationArgs = {
  nodeDataNodeId: string;
  oldParentDataNodeId: string | null;
  newParentDataNodeId: string | null;
};

type MoveDialogSessionProps = {
  target: MoveDialogTarget;
  onClose: () => void;
  projectId: string;
  templateNodeId: string;
  proposalId?: string;
  roots: DecompositionNode[];
  titlePlaceholder?: string;
  moveNodeMutation: (args: MoveMutationArgs) => Promise<unknown>;
  nodeIcon?: string;
  hasMoreRoots?: boolean;
  isFetchingMoreRoots?: boolean;
  onLoadMoreRoots?: () => void;
};

/**
 * Owns an isolated data layer for the move dialog (`useTreeDataLayer`): its own
 * children cache, its own `<ChildrenSubscriber />` instances and its own derived
 * tree. The main tree is never touched, so closing the dialog removes every subscription opened in a dialog,
 * leaving only what the user had expanded in the main tree.
 */
export const MoveDialogSession: FC<MoveDialogSessionProps> = ({
  target,
  onClose,
  projectId,
  templateNodeId,
  proposalId,
  roots,
  titlePlaceholder,
  moveNodeMutation,
  nodeIcon,
  hasMoreRoots,
  isFetchingMoreRoots,
  onLoadMoreRoots
}) => {
  const {
    openedParents,
    openParent,
    handleSnapshot,
    handleShowMore,
    loadingParentIds,
    treeItems,
    itemMetaMap,
    openParentIfNeeded,
    expandedParents,
    childrenPagination
  } = useTreeDataLayer({ roots, titlePlaceholder });

  // Seed the dialog's own expansion once: the move node's ancestors come from
  // the main tree (where the node was visible). dataNodeIds are derivable from
  // the tree ids, so no metadata lookup is needed. The `key={moveNodeId}` on
  // this component guarantees a fresh mount per dialog.
  useEffect(() => {
    target.expandedAncestors.forEach((treeId) => openParent(treeId, getDataNodeIdFromTreeId(treeId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Each ancestor's children were already fetched in the main tree (same React
  // Query cache), so revealing an ancestor whose child list is longer than
  // VISIBLE_LIMIT is a local expand, not a refetch — `handleShowMore` only hits
  // the network if the cache genuinely doesn't have the data. Runs once per
  // ancestor as soon as its snapshot arrives (`expandedParents.has` guards
  // against repeating it).
  useEffect(() => {
    target.expandedAncestors.forEach((treeId) => {
      if (!expandedParents.has(treeId) && childrenPagination[treeId]) handleShowMore(treeId);
    });
  }, [target.expandedAncestors, expandedParents, childrenPagination, handleShowMore]);

  const dialogProps = useMoveDialogSession({
    target,
    onClose,
    projectId,
    templateNodeId,
    proposalId,
    treeItems,
    itemMetaMap,
    onShowMore: handleShowMore,
    openParentIfNeeded,
    moveNodeMutation,
    nodeIcon,
    loadingParentIds,
    hasMoreRoots,
    isFetchingMoreRoots,
    onLoadMoreRoots
  });

  return (
    <>
      <ChildrenSubscribers
        openedParents={openedParents}
        projectId={projectId}
        templateNodeId={templateNodeId}
        proposalId={proposalId}
        titlePlaceholder={titlePlaceholder}
        onSnapshot={handleSnapshot}
      />
      <MoveNodeDialog {...dialogProps} />
    </>
  );
};
